import React, { useState, useEffect, useMemo } from 'react';
import { collection, doc, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import '../../styles/EditarPerfilAdmin.css';
import { useAdminContext } from '../../context/AdminContext.js';
import AdminManagementLayout from '../../components/admin/AdminManagementLayout.js';
import { db } from '../../firebase.js';
import UserList from '../../components/admin/UserList.js';

// Hook para obtener y calcular historial de saldo
function useBalanceHistory(userId, db) {
    const [history, setHistory] = useState([]);
    useEffect(() => {
        if (!userId || !db) return;
        let isMounted = true;
        async function fetchHistory() {
            const q = query(collection(db, `users/${userId}/balance_transactions`), orderBy('createdAt', 'asc'));
            const snap = await getDocs(q);
            let saldo = 0;
            const rows = [];
            snap.forEach(doc => {
                const tx = doc.data();
                const amount = Number(tx.amount || 0);
                const type = typeof tx.type === 'string' ? tx.type : '';
                const fecha = tx.createdAt && tx.createdAt.toDate ? tx.createdAt.toDate() : null;
                const saldoAntes = saldo;
                saldo += amount;
                rows.push({
                    id: doc.id,
                    fecha,
                    type,
                    amount,
                    saldoAntes,
                    saldoDespues: saldo,
                    description: tx.description || '',
                });
            });
            if (isMounted) setHistory(rows.reverse());
        }
        fetchHistory();
        return () => { isMounted = false; };
    }, [userId, db]);
    return history;
}

const MOBILE_SECTIONS = [
    { id: 'overview', label: 'Resumen' },
    { id: 'profile', label: 'Perfil' },
    { id: 'balance', label: 'Saldo' },
    { id: 'agreements', label: 'Acuerdos' },
    { id: 'referralHistory', label: 'Historial de Referidos' },
    { id: 'security', label: 'Seguridad' },
];

const GENERAL_PRESETS = [
    { id: 'bonus25', label: '+$25 Bono puntual', amount: 25, reason: 'Bono puntual' },
    { id: 'bonus50', label: '+$50 Campaña especial', amount: 50, reason: 'Bono campaña especial' },
    { id: 'bonus100', label: '+$100 Devolución manual', amount: 100, reason: 'Devolución manual de fondos' },
    { id: 'revert25', label: '-$25 Corrección parcial', amount: -25, reason: 'Corrección parcial de saldo' },
    { id: 'revert50', label: '-$50 Penalización', amount: -50, reason: 'Penalización por incumplimiento' },
    { id: 'revert100', label: '-$100 Reversión total', amount: -100, reason: 'Reversión de abono duplicado' },
];

const AGREEMENT_PRESETS = [
    { id: 'agreementBonus25', label: '+$25 Bono ROI', amount: 25, reason: 'Bono manual de ROI' },
    { id: 'agreementBonus50', label: '+$50 Ajuste adicional', amount: 50, reason: 'Ajuste adicional de rendimiento' },
    { id: 'agreementRevert25', label: '-$25 Reversión duplicado', amount: -25, reason: 'Reversión por ROI duplicado' },
    { id: 'agreementRevert50', label: '-$50 Corrección acuerdo', amount: -50, reason: 'Corrección de saldo del acuerdo' },
];

const COMMISSION_TYPES = ['commission', 'commission_bonus', 'commission_manual_fix', 'commission_level_bonus'];

const EditarPerfilAdmin = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    // Hook para historial de saldo detallado (debe ir después de declarar selectedUser)
    const balanceHistory = useBalanceHistory(selectedUser?.id, db);
    // Filtro para historial de saldos: 'all', 'incomes', 'deductions'
    const [balanceFilter, setBalanceFilter] = useState('all');
    // Nuevo estado para el patrocinador legible
    const [referrerUsername, setReferrerUsername] = useState('');
    const [profileFeedback, setProfileFeedback] = useState(null);
    const [generalFeedback, setGeneralFeedback] = useState(null);
    const [agreementFeedback, setAgreementFeedback] = useState(null);
    const [auditEntries, setAuditEntries] = useState([]);
    const [auditLoading, setAuditLoading] = useState(false);
    const [auditError, setAuditError] = useState(null);
    const [auditRefreshToken, setAuditRefreshToken] = useState(0);
    const [isAgreementModalOpen, setAgreementModalOpen] = useState(false);
    const [agreementSearch, setAgreementSearch] = useState('');
    const [isMobileLayout, setIsMobileLayout] = useState(false);
    const [activeMobileSection, setActiveMobileSection] = useState('overview');
    const [referralTotals, setReferralTotals] = useState({ amount: 0, loading: false, error: null });

    // Calcular correctamente las comisiones pagadas al usuario seleccionado
    useEffect(() => {
        if (!selectedUser || !selectedUser.id || !db) {
            setReferralTotals({ amount: 0, loading: false, error: null });
            return;
        }
        let isMounted = true;
        async function fetchCommissions() {
            setReferralTotals({ amount: 0, loading: true, error: null });
            try {
                const q = query(collection(db, `users/${selectedUser.id}/balance_transactions`));
                const snap = await getDocs(q);
                let total = 0;
                snap.forEach(doc => {
                    const tx = doc.data();
                    if (tx && COMMISSION_TYPES.includes(tx.type)) {
                        total += Number(tx.amount || 0);
                    }
                });
                if (isMounted) setReferralTotals({ amount: total, loading: false, error: null });
            } catch (err) {
                if (isMounted) setReferralTotals({ amount: 0, loading: false, error: err.message || 'Error al calcular comisiones' });
            }
        }
        fetchCommissions();
        return () => { isMounted = false; };
    }, [selectedUser, db]);
    
    // Obtenemos todo (datos y acciones) desde el contexto
    const {
        users,
        allAgreements, // Usamos la lista completa de acuerdos
        financialLog,
        processingId, // eslint-disable-line
        handleAdminProfileUpdate,
        handleAdminAdjustBalance, // eslint-disable-line
        handleAdminChangePassword
    } = useAdminContext();
    
    // Estados para los campos del formulario de perfil
    const [fullName, setFullName] = useState('');
    const [userEmail, setUserEmail] = useState('');
    const [walletAddress, setWalletAddress] = useState('');

    // --- CORRECCIÓN: Estados separados para cada tipo de ajuste ---
    // Ajuste por acuerdo
    const [agreementAdjustmentAmount, setAgreementAdjustmentAmount] = useState('');
    const [agreementAdjustmentReason, setAgreementAdjustmentReason] = useState('');
    const [selectedAgreementId, setSelectedAgreementId] = useState('');
    // Ajuste general
    const [generalAdjustmentAmount, setGeneralAdjustmentAmount] = useState('');
    const [generalAdjustmentReason, setGeneralAdjustmentReason] = useState('');

    // Computed helpers para acuerdos y referidos
    const selectedAgreement = useMemo(() => {
        if (!selectedAgreementId || !allAgreements) return null;
        return (allAgreements || []).find(a => a.id === selectedAgreementId) || null;
    }, [allAgreements, selectedAgreementId]);

    const userAgreements = useMemo(() => {
        if (!selectedUser || !Array.isArray(allAgreements)) return [];
        return (allAgreements || []).filter(a => a.userId === selectedUser.id);
    }, [allAgreements, selectedUser]);

    const approvedAgreementsByUser = useMemo(() => {
        const map = new Map();
        (allAgreements || []).forEach(a => {
            if (a.status === 'aprobado') {
                const arr = map.get(a.userId) || [];
                arr.push(a);
                map.set(a.userId, arr);
            }
        });
        return map;
    }, [allAgreements]);

    const describeAuditEntry = (entry) => entry?.description || entry?.message || '';

    const formatAuditAmount = (entry) => {
        const amount = Number(entry?.amount || 0);
        if (!entry || !('amount' in entry)) return null;
        return { label: formatCurrency(amount), positive: amount >= 0 };
    };

    const resolveAdminLabel = (entry) => entry?.adminName || entry?.adminEmail || 'Sistema';

    const applyGeneralPreset = (preset) => {
        setGeneralAdjustmentAmount(String(preset.amount));
        setGeneralAdjustmentReason(preset.reason || preset.label || '');
    };

    const applyAgreementPreset = (preset) => {
        setAgreementAdjustmentAmount(String(preset.amount));
        setAgreementAdjustmentReason(preset.reason || preset.label || '');
    };
    
    const filteredAgreements = useMemo(() => {
        const normalizedTerm = (agreementSearch || '').toLowerCase();
        if (!userAgreements || userAgreements.length === 0) return [];
        return userAgreements.filter((agreement) => {
            const baseAmount = Number(agreement.netInvestmentAmount ?? agreement.amount ?? 0);
            const currentValue = Number(agreement.currentValue ?? baseAmount);
            const progress = baseAmount > 0 ? ((currentValue - baseAmount) / baseAmount) * 100 : 0;
            const searchable = [
                agreement.id,
                baseAmount.toFixed(2),
                currentValue.toFixed(2),
                progress.toFixed(1),
            ].join(' ').toLowerCase();
            return searchable.includes(normalizedTerm);
        });
    }, [userAgreements, agreementSearch]);

    const adminDirectory = useMemo(() => {
        if (!users) return new Map();
        return new Map(users.map(user => [user.id, user]));
    }, [users]);

    // Inicializar campos del formulario cuando cambie el usuario seleccionado
    useEffect(() => {
        if (!selectedUser) {
            setFullName('');
            setUserEmail('');
            setWalletAddress('');
            setReferrerUsername('');
            return;
        }

        // Inicializar valores desde selectedUser
        setFullName(selectedUser.fullName || selectedUser.userEmail || '');
        setUserEmail(selectedUser.userEmail || '');
        setWalletAddress(selectedUser.walletAddress || selectedUser.walletAddr || '');

        // Resolver username del patrocinador si existe
        const sponsorId = selectedUser.referrerId || selectedUser.sponsorId || selectedUser.referrer;
        if (sponsorId && adminDirectory && adminDirectory.get(sponsorId)) {
            const sponsor = adminDirectory.get(sponsorId);
            setReferrerUsername(sponsor.username || sponsor.fullName || sponsor.userEmail || '');
        } else if (selectedUser.referrerUsername) {
            setReferrerUsername(selectedUser.referrerUsername);
        } else {
            setReferrerUsername('');
        }
    }, [selectedUser, adminDirectory]);

    const showSection = (sectionId) => (!isMobileLayout || activeMobileSection === sectionId);

    const normalizeValue = (value) => (value || '').trim();

    const isProfileDirty = useMemo(() => {
        if (!selectedUser) return false;
        return (
            normalizeValue(fullName) !== normalizeValue(selectedUser.fullName) ||
            normalizeValue(userEmail) !== normalizeValue(selectedUser.userEmail) ||
            normalizeValue(walletAddress) !== normalizeValue(selectedUser.walletAddress) ||
            normalizeValue(referrerUsername) !== normalizeValue(resolveSponsorUsername(selectedUser, users))
        );
    }, [fullName, userEmail, walletAddress, referrerUsername, selectedUser, users]);

    // Helper para obtener el username real del patrocinador
    function resolveSponsorUsername(user, usersList) {
        if (!user) return '';
        const sponsorId = user.referrerId || user.sponsorId || user.referrer;
        if (sponsorId && Array.isArray(usersList)) {
            const sponsor = usersList.find(u => u.id === sponsorId);
            return sponsor?.username || sponsor?.fullName || sponsor?.userEmail || '';
        }
        return user.referrerUsername || '';
    }

    const isGeneralReady = useMemo(() => {
        if (!selectedUser) return false;
        const amount = parseFloat(generalAdjustmentAmount);
        return !Number.isNaN(amount) && amount !== 0 && normalizeValue(generalAdjustmentReason).length >= 5;
    }, [selectedUser, generalAdjustmentAmount, generalAdjustmentReason]);

    const isAgreementReady = useMemo(() => {
        if (!selectedUser || !selectedAgreement) return false;
        const amount = parseFloat(agreementAdjustmentAmount);
        return !Number.isNaN(amount) && amount !== 0 && normalizeValue(agreementAdjustmentReason).length >= 5;
    }, [selectedUser, selectedAgreement, agreementAdjustmentAmount, agreementAdjustmentReason]);

    // Precompute métricas clave del usuario seleccionado para mostrarlas en el resumen.
    const summaryStats = useMemo(() => {
        if (!selectedUser) {
            return null;
        }

        const balance = Number(selectedUser.withdrawableBalance || 0);
        const activeAgreements = userAgreements.length;
        const totalInvested = userAgreements.reduce((sum, agreement) => sum + Number(agreement.netInvestmentAmount ?? agreement.amount ?? 0), 0);
        const totalCurrentValue = userAgreements.reduce((sum, agreement) => sum + Number(agreement.currentValue ?? agreement.netInvestmentAmount ?? agreement.amount ?? 0), 0);

        return {
            balance,
            activeAgreements,
            totalInvested,
            totalCurrentValue,
        };
    }, [selectedUser, userAgreements]);

    // Tomamos los registros financieros recientes del usuario para tener contexto antes de editar.
    const recentMovements = useMemo(() => {
        if (!selectedUser) {
            return [];
        }

        const entries = (financialLog || [])
            .filter(entry => entry.userId === selectedUser.id)
            .map(entry => {
                const timestamp = entry.createdAt?.toDate ? entry.createdAt.toDate() : (entry.createdAt instanceof Date ? entry.createdAt : null);
                return { ...entry, timestamp };
            })
            .sort((a, b) => {
                const timeA = a.timestamp ? a.timestamp.getTime() : 0;
                const timeB = b.timestamp ? b.timestamp.getTime() : 0;
                return timeB - timeA;
            });

        return entries.slice(0, 4);
    }, [financialLog, selectedUser]);

    const generalPreview = useMemo(() => {
        if (!selectedUser) {
            return null;
        }

        const baseBalance = Number(selectedUser.withdrawableBalance || 0);
        const amount = parseFloat(generalAdjustmentAmount);
        const delta = Number.isNaN(amount) ? 0 : amount;

        return {
            currentBalance: baseBalance,
            delta,
            projectedBalance: baseBalance + delta,
        };
    }, [selectedUser, generalAdjustmentAmount]);

    const agreementPreview = useMemo(() => {
        if (!selectedAgreement) {
            return null;
        }

        const baseAmount = Number(selectedAgreement.netInvestmentAmount ?? selectedAgreement.amount ?? 0);
        const currentValue = Number(selectedAgreement.currentValue ?? baseAmount);
        const capAmount = baseAmount * 2;
        const amount = parseFloat(agreementAdjustmentAmount);
        const delta = Number.isNaN(amount) ? 0 : amount;

        return {
            baseAmount,
            currentValue,
            capAmount,
            delta,
            projectedValue: currentValue + delta,
        };
    }, [selectedAgreement, agreementAdjustmentAmount]);

    const referralInsights = useMemo(() => {
        if (!selectedUser || !Array.isArray(users) || users.length === 0) {
            return null;
        }

        // --- OPTIMIZACIÓN: Iterar una sola vez para clasificar referidos ---
        const direct = [];
        const secondLevel = [];
        const thirdLevel = [];

        users.forEach(user => {
            if (user.status === 'deactivated') return;

            if (user.referrerId === selectedUser.id) {
                direct.push(user);
            }
            if (user.level2ReferrerId === selectedUser.id) {
                secondLevel.push(user);
            }
            if (user.level3ReferrerId === selectedUser.id) {
                thirdLevel.push(user);
            }
        });


        // Usar solo acuerdos aprobados para cada referido
        const directWithStats = direct.map((referral) => {
            const agreements = Array.isArray(approvedAgreementsByUser)
                ? []
                : (approvedAgreementsByUser.get(referral.id) || []);
            const approved = agreements.filter(a => a.status === 'aprobado');
            const invested = approved.reduce((sum, agreement) => sum + Number(agreement.netInvestmentAmount ?? agreement.amount ?? 0), 0);
            return {
                ...referral,
                invested,
                agreementsCount: approved.length,
                userRole: referral.userRole || referral.role || 'Usuario',
            };
        }).sort((a, b) => b.invested - a.invested);

        const directVolume = directWithStats.reduce((sum, referral) => sum + referral.invested, 0);
        const rawTeamSize = selectedUser.teamSize ?? selectedUser.totalTeamMembers ?? selectedUser.totalReferrals;
        const totalTeam = Number.isFinite(Number(rawTeamSize))
            ? Number(rawTeamSize)
            : direct.length + secondLevel.length + thirdLevel.length;

        return {
            direct,
            secondLevel,
            thirdLevel,
            earnings: referralTotals.amount,
            directVolume,
            totalTeam,
            topDirect: directWithStats.slice(0, 5),
            hasMoreDirect: directWithStats.length > 5,
            earningsLoading: referralTotals.loading,
            earningsError: referralTotals.error,
        };
    }, [selectedUser, users, approvedAgreementsByUser, referralTotals]);

    const formatCurrency = (value) => {
        const amount = Number(value || 0);
        return amount.toLocaleString('es-ES', { style: 'currency', currency: 'USD', currencyDisplay: 'symbol' });
    };

    const formatSignedCurrency = (value) => {
        const amount = Number(value || 0);
        if (amount === 0) {
            return formatCurrency(0);
        }
        return `${amount > 0 ? '+' : '-'}${formatCurrency(Math.abs(amount))}`;
    };

    const getAuditTitle = (entry) => {
        switch (entry?.type) {
            case 'profile_update':
                return 'Actualización de perfil';
            case 'balance_adjustment':
                return 'Ajuste de saldo';
            default:
                return entry?.type || 'Registro';
        }
    };


    useEffect(() => {
        let isMounted = true;
        const fetchAuditEntries = async () => {
            if (!isMounted) return;
            setAuditLoading(true);
            setAuditError(null);
            if (!selectedUser?.id) {
                if (isMounted) {
                    setAuditEntries([]);
                    setAuditLoading(false);
                }
                return;
            }

            try {
                const auditRef = collection(doc(db, 'users', selectedUser.id, 'profile', 'data'), 'admin_audit');
                const auditQuery = query(auditRef, orderBy('createdAt', 'desc'), limit(8));
                const snapshot = await getDocs(auditQuery);

                if (!isMounted) {
                    return;
                }

                const entries = snapshot.docs.map(docSnap => {
                    const data = docSnap.data();
                    const timestamp = data.createdAt?.toDate ? data.createdAt.toDate() : (data.createdAt instanceof Date ? data.createdAt : null);
                    return {
                        id: docSnap.id,
                        ...data,
                        timestamp,
                    };
                });

                setAuditEntries(entries);
            } catch (error) {
                console.error('Error al cargar el historial de auditoría:', error);
                if (isMounted) {
                    setAuditError('No se pudo cargar el historial de cambios.');
                    setAuditEntries([]);
                }
            } finally {
                if (isMounted) {
                    setAuditLoading(false);
                }
            }
        };

        fetchAuditEntries();

        return () => {
            isMounted = false;
        };
    }, [selectedUser, auditRefreshToken]);

    useEffect(() => {
        let isMounted = true;
        const userId = selectedUser?.id;

        const fetchReferralTotals = async () => {
            if (!userId) {
                if (isMounted) {
                    setReferralTotals({ amount: 0, loading: false, error: null });
                }
                return;
            }

            setReferralTotals((prev) => ({ ...prev, loading: true, error: null }));

            try {
                const transactionsRef = collection(db, `users/${userId}/balance_transactions`);
                const commissionsQuery = query(transactionsRef, where('type', 'in', COMMISSION_TYPES));
                const snapshot = await getDocs(commissionsQuery);
                const total = snapshot.docs.reduce((sum, docSnap) => sum + Number(docSnap.data().amount || 0), 0);

                if (isMounted) {
                    setReferralTotals({ amount: total, loading: false, error: null });
                }
            } catch (error) {
                console.error('Error al cargar comisiones de referidos:', error);
                if (isMounted) {
                    setReferralTotals({ amount: 0, loading: false, error });
                }
            }
        };

        fetchReferralTotals();

        return () => {
            isMounted = false;
        };
    }, [selectedUser?.id, auditRefreshToken]);

    // Función para guardar cambios básicos del perfil
    const handleProfileUpdate = async () => {
        if (!selectedUser || !isProfileDirty) {
            setProfileFeedback({ type: 'info', message: 'No hay cambios por guardar.' });
            return;
        }

        // Campos básicos a actualizar
        const updates = {
            fullName: normalizeValue(fullName),
            userEmail: normalizeValue(userEmail),
            walletAddress: normalizeValue(walletAddress),
        };

        // Resolver el patrocinador: puede ser un username, un email, o un link con ?ref=
        const rawRefInput = normalizeValue(referrerUsername);
        if (rawRefInput && rawRefInput.length > 0) {
            let refValue = rawRefInput;
            try {
                const qIdx = rawRefInput.indexOf('?ref=');
                if (qIdx !== -1) {
                    refValue = rawRefInput.substring(qIdx + 5);
                    const amp = refValue.indexOf('&');
                    if (amp !== -1) refValue = refValue.substring(0, amp);
                    refValue = decodeURIComponent(refValue);
                }
            } catch (e) {
                // ignore parse errors
            }

            const sponsor = (users || []).find(u => {
                if (!u) return false;
                const candidate = (u.username || u.userEmail || '').toLowerCase();
                return candidate === refValue.toLowerCase();
            }) || null;

            if (sponsor) {
                updates.referrerId = sponsor.id;
                updates.referrerUsername = sponsor.username || sponsor.userEmail || '';
            } else {
                updates.referrerUsername = refValue;
            }
        } else {
            // Input vacío -> eliminar patrocinador
            updates.referrerId = null;
            updates.referrerUsername = '';
        }

        const result = await handleAdminProfileUpdate(selectedUser.id, updates, selectedUser);

        if (result?.success) {
            setProfileFeedback({ type: 'success', message: 'Perfil actualizado correctamente.' });
            setSelectedUser(prev => (prev && prev.id === selectedUser.id ? { ...prev, ...updates } : prev));
            setAuditRefreshToken(prev => prev + 1);
        } else if (result?.reason === 'no-changes') {
            setProfileFeedback({ type: 'info', message: 'No hay cambios por guardar.' });
        } else {
            const errorMessage = result?.error?.message || 'No se pudo actualizar el perfil.';
            setProfileFeedback({ type: 'error', message: errorMessage });
        }
    };

    // Nueva función para ajustar el saldo (por acuerdo)
    const handleBalanceAdjustment = async () => {
        if (!selectedUser || !selectedAgreement) {
            setAgreementFeedback({ type: 'error', message: 'Selecciona un acuerdo para continuar.' });
            return;
        }

        const amount = parseFloat(agreementAdjustmentAmount);
        if (Number.isNaN(amount) || amount === 0) {
            setAgreementFeedback({ type: 'error', message: 'Ingresa un monto válido distinto de cero.' });
            return;
        }

        if (normalizeValue(agreementAdjustmentReason).length < 5) {
            setAgreementFeedback({ type: 'error', message: 'El motivo debe contener al menos 5 caracteres.' });
            return;
        }

        const baseAmount = selectedAgreement.netInvestmentAmount || selectedAgreement.amount || 0;
        const currentValue = selectedAgreement.currentValue || baseAmount;
        const capAmount = baseAmount * 2;

        if (amount > 0 && (currentValue + amount > capAmount)) {
            const maxAdd = Math.max(capAmount - currentValue, 0).toFixed(2);
            setAgreementFeedback({ type: 'error', message: `El monto excede el tope del 200%. Máximo disponible: $${maxAdd}.` });
            return;
        }

        if (amount < 0 && (currentValue + amount < baseAmount)) {
            setAgreementFeedback({ type: 'error', message: `No puedes reducir por debajo del capital inicial de $${baseAmount.toFixed(2)}.` });
            return;
        }

        const actionText = amount > 0 ? 'añadir' : 'restar';
        const absAmount = Math.abs(amount);
        const confirmMessage = `¿Deseas ${actionText} $${absAmount.toFixed(2)} al acuerdo #${selectedAgreement.id.substring(0, 6)} de ${selectedUser.fullName}?`;

        if (!window.confirm(confirmMessage)) {
            return;
        }

        const result = await handleAdminAdjustBalance(selectedUser.id, amount, normalizeValue(agreementAdjustmentReason), selectedAgreement.id);

        if (result?.success) {
            setAgreementFeedback({ type: 'success', message: result.message || 'Ajuste registrado correctamente.' });
            setAgreementAdjustmentAmount('');
            setAgreementAdjustmentReason('');
            setSelectedAgreementId('');
            setAgreementModalOpen(false);
            setAgreementSearch('');
            setAuditRefreshToken(prev => prev + 1);
        } else {
            const errorMessage = result?.error?.message || 'No se pudo procesar el ajuste.';
            setAgreementFeedback({ type: 'error', message: errorMessage });
        }
    };

    // --- NUEVA FUNCIÓN: Ajuste de saldo general ---
    const handleGeneralBalanceAdjustment = async () => {
        if (!selectedUser) {
            setGeneralFeedback({ type: 'error', message: 'Selecciona un usuario antes de ajustar el saldo.' });
            return;
        }

        const amount = parseFloat(generalAdjustmentAmount);
        if (Number.isNaN(amount) || amount === 0) {
            setGeneralFeedback({ type: 'error', message: 'Ingresa un monto válido distinto de cero.' });
            return;
        }

        if (normalizeValue(generalAdjustmentReason).length < 5) {
            setGeneralFeedback({ type: 'error', message: 'El motivo debe contener al menos 5 caracteres.' });
            return;
        }

        const actionText = amount > 0 ? 'añadir' : 'restar';
        const absAmount = Math.abs(amount);
        const confirmMessage = `¿Deseas ${actionText} $${absAmount.toFixed(2)} al saldo disponible para retiro de ${selectedUser.fullName}?`;

        if (!window.confirm(confirmMessage)) {
            return;
        }

        const result = await handleAdminAdjustBalance(selectedUser.id, amount, normalizeValue(generalAdjustmentReason), null);

        if (result?.success) {
            setGeneralFeedback({ type: 'success', message: result.message || 'Ajuste aplicado correctamente.' });
            setGeneralAdjustmentAmount('');
            setGeneralAdjustmentReason('');
            setSelectedUser(prev => (prev && prev.id === selectedUser.id ? {
                ...prev,
                withdrawableBalance: (Number(prev.withdrawableBalance) || 0) + amount,
            } : prev));
            setAuditRefreshToken(prev => prev + 1);
        } else {
            const errorMessage = result?.error?.message || 'No se pudo procesar el ajuste.';
            setGeneralFeedback({ type: 'error', message: errorMessage });
        }
    };
    
    // Función para cambiar la contraseña
    const handleChangePassword = () => {
        if (!selectedUser) return;
        handleAdminChangePassword(selectedUser.id, selectedUser.userEmail);
    };

    const mobileQuickActions = (!isMobileLayout || !selectedUser)
        ? []
        : [
            {
                id: 'quick-save',
                label: 'Guardar perfil',
                onClick: handleProfileUpdate,
                disabled: processingId === selectedUser.id || !isProfileDirty,
            },
            {
                id: 'quick-balance',
                label: 'Saldo',
                onClick: () => setActiveMobileSection('balance'),
                disabled: false,
            },
            {
                id: 'quick-agreement',
                label: 'Ajuste acuerdo',
                onClick: () => {
                    setActiveMobileSection('agreements');
                    if (!selectedAgreement && userAgreements.length > 0) {
                        setAgreementSearch('');
                        setAgreementModalOpen(true);
                    }
                },
                disabled: userAgreements.length === 0,
            },
            {
                id: 'quick-referrals',
                label: 'Referidos',
                onClick: () => setActiveMobileSection('referrals'),
                disabled: false,
            },
            {
                id: 'quick-security',
                label: 'Clave temporal',
                onClick: handleChangePassword,
                disabled: processingId === selectedUser.id,
            },
        ];


    const filteredUsers = users.filter(user => 
        user.status !== 'deactivated' && (
            (user.fullName?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
            (user.userEmail?.toLowerCase() || '').includes(searchTerm.toLowerCase())
        )
    );

    // Componentes auxiliares movidos aquí (antes del return) para evitar insertarlos dentro del JSX
    function ReferralDirectsTable({ direct }) {
        const [filter, setFilter] = useState({ name: '', email: '', country: '', date: '' });
        const [search, setSearch] = useState('');
        const filtered = (direct || []).filter(ref => {
            return (
                (!filter.name || (ref.username || '').toLowerCase().includes(filter.name.toLowerCase())) &&
                (!filter.email || (ref.userEmail || '').toLowerCase().includes(filter.email.toLowerCase())) &&
                (!filter.country || (ref.country || '').toLowerCase().includes(filter.country.toLowerCase())) &&
                (!filter.date || (ref.createdAt && new Date(ref.createdAt.seconds*1000).toLocaleDateString().includes(filter.date))) &&
                (!search || (
                    (ref.username || '').toLowerCase().includes(search.toLowerCase()) ||
                    (ref.userEmail || '').toLowerCase().includes(search.toLowerCase())
                ))
            );
        });
        return (
            <div className="referral-table-wrapper">
                <div style={{marginBottom:'0.5rem', display:'flex', gap:'0.5rem', flexWrap:'wrap'}}>
                    <input placeholder="Buscar..." value={search} onChange={e=>setSearch(e.target.value)} />
                    <input placeholder="Filtrar por nombre" value={filter.name} onChange={e=>setFilter(f=>({...f, name:e.target.value}))} />
                    <input placeholder="Filtrar por correo" value={filter.email} onChange={e=>setFilter(f=>({...f, email:e.target.value}))} />
                    <input placeholder="Filtrar por país" value={filter.country} onChange={e=>setFilter(f=>({...f, country:e.target.value}))} />
                    <input placeholder="Filtrar por fecha" value={filter.date} onChange={e=>setFilter(f=>({...f, date:e.target.value}))} />
                </div>
                <table className="referral-table">
                    <thead>
                        <tr>
                            <th>Usuario</th>
                            <th>Correo</th>
                            <th>Participación</th>
                            <th>Acuerdos</th>
                            <th>País</th>
                            <th>Fecha de Alta</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length > 0 ? filtered.map((referral) => (
                            <tr key={referral.id}>
                                <td>{referral.username || referral.fullName || referral.userEmail}</td>
                                <td>{referral.userEmail}</td>
                                <td>{formatCurrency(referral.invested)}</td>
                                <td>{referral.agreementsCount}</td>
                                <td>{referral.country || '-'}</td>
                                <td>{referral.createdAt ? new Date(referral.createdAt.seconds*1000).toLocaleDateString() : '-'}</td>
                            </tr>
                        )) : (
                            <tr><td colSpan={6} className="movement-empty">No se registran referidos directos para este usuario.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        );
    }

    function ReferralInvestmentsTable({ investments }) {
        if (!investments || investments.length === 0) {
            return <p className="movement-empty">No hay participaciones de referidos registradas.</p>;
        }
        return (
            <div className="referral-table-wrapper">
                <table className="referral-table">
                    <thead>
                        <tr>
                            <th>Referido</th>
                            <th>Correo</th>
                            <th>Monto</th>
                            <th>Estado</th>
                            <th>Fecha</th>
                        </tr>
                    </thead>
                    <tbody>
                        {investments.map((inv, idx) => (
                            <tr key={idx}>
                                <td>{inv.username || inv.fullName || inv.userEmail}</td>
                                <td>{inv.userEmail}</td>
                                <td>{formatCurrency(inv.amount)}</td>
                                <td>{inv.status}</td>
                                <td>{inv.date ? new Date(inv.date.seconds*1000).toLocaleDateString() : '-'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    function ReferralChangesHistory({ history }) {
        if (!history || history.length === 0) {
            return <p className="movement-empty">No hay historial de cambios registrado.</p>;
        }
        return (
            <div className="referral-table-wrapper">
                <table className="referral-table">
                    <thead>
                        <tr>
                            <th>Usuario</th>
                            <th>Acción</th>
                            <th>Referente</th>
                            <th>Fecha</th>
                        </tr>
                    </thead>
                    <tbody>
                        {history.map((item, idx) => (
                            <tr key={idx}>
                                <td>{item.username || item.userEmail}</td>
                                <td>{item.action}</td>
                                <td>{item.sponsorUsername || item.sponsorEmail}</td>
                                <td>{item.date ? new Date(item.date.seconds*1000).toLocaleDateString() : '-'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    // Mostrar solo los 10 últimos movimientos según el filtro
    const filteredBalanceHistory = useMemo(() => {
        let filtered = balanceHistory;
        if (balanceFilter === 'incomes') {
            filtered = balanceHistory.filter(row => row.amount > 0);
        } else if (balanceFilter === 'deductions') {
            filtered = balanceHistory.filter(row => row.amount < 0);
        }
        return filtered.slice(0, 10);
    }, [balanceHistory, balanceFilter]);

    return (
        <div className="main-panel">
            <h2 className="section-title">Editar Perfiles de Usuario</h2>
            <AdminManagementLayout
                listPanel={
                    <UserList
                        title="Usuarios Activos"
                        users={filteredUsers}
                        searchTerm={searchTerm}
                        onSearchChange={(e) => setSearchTerm(e.target.value)}
                        selectedUserId={selectedUser?.id}
                        onSelectUser={setSelectedUser}
                    />
                }
                detailsPanel={
                    selectedUser ? (
                        <div className="admin-edit-container">
                            {isMobileLayout && (
                                <>
                                    <div className="mobile-step-nav">
                                        {MOBILE_SECTIONS.map((section) => (
                                            <button
                                                key={section.id}
                                                type="button"
                                                className={`step-chip ${activeMobileSection === section.id ? 'active' : ''}`}
                                                onClick={() => setActiveMobileSection(section.id)}
                                            >
                                                {section.label}
                                            </button>
                                        ))}
                                    </div>
                                    {mobileQuickActions.length > 0 && (
                                        <div className="mobile-quick-actions">
                                            {mobileQuickActions.map((action) => (
                                                <button
                                                    key={action.id}
                                                    type="button"
                                                    className="quick-action-button"
                                                    onClick={action.onClick}
                                                    disabled={action.disabled}
                                                >
                                                    {action.label}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                            {summaryStats && showSection('overview') && (
                                <>
                                <div className="admin-edit-card admin-summary-card">
                                    <div className="summary-header">
                                        <div className="summary-avatar">{selectedUser.fullName?.charAt(0)?.toUpperCase() || 'U'}</div>
                                        <div className="summary-user-meta">
                                            <h3>{selectedUser.fullName || selectedUser.userEmail}</h3>
                                            <p>{selectedUser.userEmail}</p>
                                            {selectedUser.userRole && (
                                                <span className="summary-role">{selectedUser.userRole}</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="summary-grid">
                                        <div className="summary-stat">
                                            <span className="summary-label">Disponible para retiro</span>
                                            <strong>{formatCurrency(summaryStats.balance)}</strong>
                                        </div>
                                        <div className="summary-stat">
                                            <span className="summary-label">Acuerdos activos</span>
                                            <strong>{summaryStats.activeAgreements}</strong>
                                        </div>
                                        <div className="summary-stat">
                                            <span className="summary-label">Capital invertido</span>
                                            <strong>{formatCurrency(summaryStats.totalInvested)}</strong>
                                        </div>
                                        <div className="summary-stat">
                                            <span className="summary-label">Valor actual</span>
                                            <strong>{formatCurrency(summaryStats.totalCurrentValue)}</strong>
                                        </div>
                                    </div>
                                    <div className="summary-movements">
                                        <h4>Últimos movimientos</h4>
                                        {recentMovements.length > 0 ? (
                                            <ul className="movement-list">
                                                {recentMovements.map(movement => {
                                                    const amount = Number(movement.amount || 0);
                                                    const sign = amount >= 0 ? '+' : '-';
                                                    const formattedAmount = `${sign}${formatCurrency(Math.abs(amount))}`;
                                                    const formattedDate = movement.timestamp ? movement.timestamp.toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' }) : 'Fecha no disponible';
                                                    return (
                                                        <li key={movement.id} className="movement-item">
                                                            <div className="movement-meta">
                                                                <span className="movement-description">{movement.description || 'Movimiento sin descripción'}</span>
                                                                <small className="movement-date">{formattedDate}</small>
                                                            </div>
                                                            <span className={`movement-amount ${amount >= 0 ? 'positive' : 'negative'}`}>{formattedAmount}</span>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        ) : (
                                            <p className="movement-empty">No hay movimientos recientes para este usuario.</p>
                                        )}
                                    </div>
                                </div>
                                {/* --- NUEVA SECCIÓN: Historial de Saldos (detalle) --- */}
                                <div className="admin-edit-card balance-history-card">
                                    <div className="card-header-row" style={{display:'flex', alignItems:'center', justifyContent:'space-between', gap:8}}>
                                        <h3 className="section-subtitle">Historial de Saldos (detalle)</h3>
                                        <div style={{display:'flex', gap:4}}>
                                            <button
                                                className={balanceFilter==='all' ? 'balance-filter-btn active' : 'balance-filter-btn'}
                                                onClick={()=>setBalanceFilter('all')}
                                            >Todos</button>
                                            <button
                                                className={balanceFilter==='incomes' ? 'balance-filter-btn active' : 'balance-filter-btn'}
                                                onClick={()=>setBalanceFilter('incomes')}
                                            >Ingresos</button>
                                            <button
                                                className={balanceFilter==='deductions' ? 'balance-filter-btn active' : 'balance-filter-btn'}
                                                onClick={()=>setBalanceFilter('deductions')}
                                            >Deducciones</button>
                                        </div>
                                    </div>
                                    <ModernBalanceHistoryTable history={filteredBalanceHistory} />
                                </div>
                                </>
                            )}
                            {showSection('overview') && (
                                <div className="admin-edit-card audit-card">
                                <div className="card-header-row">
                                    <h3 className="section-subtitle">Historial reciente</h3>
                                </div>
                                {auditLoading ? (
                                    <p className="movement-empty">Cargando historial...</p>
                                ) : auditEntries.length > 0 ? (
                                    <ul className="audit-list">
                                        {auditEntries.map(entry => {
                                            const title = getAuditTitle(entry);
                                            const description = describeAuditEntry(entry);
                                            const amountInfo = formatAuditAmount(entry);
                                            const adminLabel = resolveAdminLabel(entry);
                                            const formattedDate = entry.timestamp ? entry.timestamp.toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' }) : 'Fecha no disponible';
                                            return (
                                                <li key={entry.id} className="audit-item">
                                                    <div className="audit-item__meta">
                                                        <span className="audit-item__title">{title}</span>
                                                        <small className="audit-item__date">{formattedDate}</small>
                                                    </div>
                                                    <p className="audit-item__description">{description}</p>
                                                    <div className="audit-item__footer">
                                                        <span className="audit-item__author">Por {adminLabel}</span>
                                                        {amountInfo && (
                                                            <span className={`audit-item__amount ${amountInfo.positive ? 'positive' : 'negative'}`}>
                                                                {amountInfo.label}
                                                            </span>
                                                        )}
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                ) : (
                                    <p className="movement-empty">No hay acciones recientes registradas.</p>
                                )}
                                {auditError && <div className="feedback-inline feedback-inline--error">{auditError}</div>}
                                </div>
                            )}
                            {showSection('profile') && (
                                <div className="admin-edit-card">
                                    <div className="card-header-row">
                                        <h3 className="section-subtitle">Datos del Perfil de: {selectedUser.fullName}</h3>
                                        {isProfileDirty && <span className="pending-badge">Pendiente de guardar</span>}
                                    </div>
                                    <div className="form-grid">
                                        <div className="input-group"><label>Nombre Completo</label><input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} /></div>
                                        <div className="input-group"><label>Correo Electrónico</label><input type="email" value={userEmail} onChange={(e) => setUserEmail(e.target.value)} /></div>
                                        <div className="input-group">
                                            <label>Código de referido (ID de este usuario)</label>
                                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                                <input
                                                    type="text"
                                                    value={selectedUser.id || ''}
                                                    readOnly
                                                    style={{ background: '#f3f4f6', color: '#555', flex: 1 }}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => { navigator.clipboard.writeText(selectedUser.id || ''); }}
                                                    style={{ padding: '0.3rem 0.7rem', cursor: 'pointer' }}
                                                >
                                                    Copiar
                                                </button>
                                            </div>
                                        </div>
                                        <div className="input-group">
                                            <label>Link de referido (con username)</label>
                                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                                <input
                                                    type="text"
                                                    value={`https://lignumsgroup.com/register?ref=${selectedUser.username || ''}`}
                                                    readOnly
                                                    style={{ background: '#f3f4f6', color: '#555', flex: 1 }}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => { navigator.clipboard.writeText(`https://lignumsgroup.com/register?ref=${selectedUser.username || ''}`); }}
                                                    style={{ padding: '0.3rem 0.7rem', cursor: 'pointer' }}
                                                >
                                                    Copiar
                                                </button>
                                            </div>
                                        </div>
                                        <div className="input-group full-width"><label>Dirección de Billetera</label><input type="text" value={walletAddress} onChange={(e) => setWalletAddress(e.target.value)} /></div>
                                        <div className="input-group full-width">
                                            <label>Vinculador (username)</label>
                                            <input type="text" value={referrerUsername || ''} onChange={e => setReferrerUsername(e.target.value)} placeholder="Username del vinculador" />
                                        </div>
                                    </div>
                                    <button
                                        onClick={handleProfileUpdate}
                                        className="login-button"
                                        style={{ width: '100%', marginTop: '1rem' }}
                                        disabled={processingId === selectedUser.id || !isProfileDirty}
                                    >
                                        {processingId === selectedUser.id ? 'Guardando...' : 'Guardar Cambios'}
                                    </button>
                                    {profileFeedback && (
                                        <div className={`feedback-inline feedback-inline--${profileFeedback.type}`}>
                                            {profileFeedback.message}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* --- NUEVA TARJETA DE AJUSTE GENERAL --- */}
                            {showSection('balance') && (
                                <div className="admin-edit-card">
                                    <h3 className="section-subtitle">Ajuste de Saldo General</h3>
                                    <p className="card-description">Añade o resta fondos directamente al <strong>saldo disponible para retiro</strong> del usuario, sin afectar ningún acuerdo.</p>
                                    <div className="preset-chip-group">
                                        {GENERAL_PRESETS.map((preset) => (
                                            <button
                                                key={preset.id}
                                                type="button"
                                                className="preset-chip"
                                                onClick={() => applyGeneralPreset(preset)}
                                            >
                                                {preset.label}
                                            </button>
                                        ))}
                                    </div>
                                    <div className="form-grid">
                                        <div className="input-group">
                                            <label>Monto a Ajustar (+/- $)</label>
                                            <input type="number" placeholder="Ej: 100 o -50" value={generalAdjustmentAmount} onChange={(e) => setGeneralAdjustmentAmount(e.target.value)} />
                                        </div>
                                        <div className="input-group full-width">
                                            <label>Motivo del Ajuste (Bono, Corrección, etc.)</label>
                                            <input type="text" placeholder="Ej: Bono por desempeño" value={generalAdjustmentReason} onChange={(e) => setGeneralAdjustmentReason(e.target.value)} />
                                        </div>
                                    </div>
                                    {generalPreview && (
                                        <div className="balance-preview">
                                            <div>
                                                <span>Saldo actual</span>
                                                <strong>{formatCurrency(generalPreview.currentBalance)}</strong>
                                            </div>
                                            <div>
                                                <span>Cambio</span>
                                                <strong className={generalPreview.delta >= 0 ? 'positive' : 'negative'}>{formatSignedCurrency(generalPreview.delta)}</strong>
                                            </div>
                                            <div>
                                                <span>Saldo estimado</span>
                                                <strong>{formatCurrency(generalPreview.projectedBalance)}</strong>
                                            </div>
                                        </div>
                                    )}
                                    <button
                                        onClick={handleGeneralBalanceAdjustment}
                                        className="login-button"
                                        style={{ width: '100%', marginTop: '1rem', backgroundColor: '#4a5568' }}
                                        disabled={processingId === selectedUser.id || !isGeneralReady}
                                    >
                                        {processingId === selectedUser.id ? 'Procesando...' : 'Realizar Ajuste General'}
                                    </button>
                                    {generalFeedback && (
                                        <div className={`feedback-inline feedback-inline--${generalFeedback.type}`}>
                                            {generalFeedback.message}
                                        </div>
                                    )}
                                </div>
                            )}

                            {showSection('referrals') && (
                                <div className="admin-edit-card">
                                    <div className="card-header-row">
                                        <h3 className="section-subtitle">Referidos y resultados</h3>
                                    </div>
                                    {referralInsights ? (
                                        <>
                                            <div className="referral-summary-grid">
                                                <div>
                                                    <span>Referidos directos</span>
                                                    <strong>{referralInsights.direct.length}</strong>
                                                </div>
                                                <div>
                                                    <span>Equipo total estimado</span>
                                                    <strong>{referralInsights.totalTeam}</strong>
                                                </div>
                                                <div>
                                                    <span>Reconocimiento por vinculación (comisiones)</span>
                                                    <strong>{formatCurrency(referralInsights.earnings)}</strong>
                                                </div>
                                                <div>
                                                    <span>Volumen directo</span>
                                                    <strong>{formatCurrency(referralInsights.directVolume)}</strong>
                                                </div>
                                            </div>
                                            {referralInsights.earningsLoading && (
                                                <p className="referral-footnote">Calculando resultados recientes...</p>
                                            )}
                                            {referralInsights.earningsError && (
                                                <p className="referral-footnote referral-footnote--error">No se pudieron cargar las comisiones. Reintenta más tarde.</p>
                                            )}
                                            {referralInsights.topDirect.length > 0 ? (
                                                <div className="referral-table-wrapper">
                                                    <table className="referral-table">
                                                        <thead>
                                                            <tr>
                                                                <th>Usuario</th>
                                                                <th>Correo</th>
                                                                <th>Capital invertido</th>
                                                                <th>Acuerdos activos</th>
                                                                <th>Rol</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {referralInsights.topDirect.map((referral) => (
                                                                <tr key={referral.id}>
                                                                    <td>{referral.fullName || referral.userEmail || referral.id}</td>
                                                                    <td>{referral.userEmail || '—'}</td>
                                                                    <td>{formatCurrency(referral.invested || 0)}</td>
                                                                    <td>{referral.agreementsCount || 0}</td>
                                                                    <td>{referral.userRole || 'Usuario'}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                    {referralInsights.hasMoreDirect && (
                                                        <p className="referral-footnote">Mostrando los primeros {referralInsights.topDirect.length} referidos directos.</p>
                                                    )}
                                                </div>
                                            ) : (
                                                <p className="movement-empty">No se registran referidos directos para este usuario.</p>
                                            )}
                                        </>
                                    ) : (
                                        <p className="movement-empty">No hay datos de referidos disponibles.</p>
                                    )}
                                </div>
                            )}

                            {showSection('agreements') && (
                                <div className="admin-edit-card">
                                    <div className="card-header-row">
                                        <h3 className="section-subtitle">Ajuste de Saldo Manual por Acuerdo</h3>
                                        {selectedAgreement && <span className="pending-badge pending-badge--info">Acuerdo seleccionado</span>}
                                    </div>
                                    <div className="input-group full-width agreement-selector-group">
                                        <label>Selecciona un Acuerdo</label>
                                        <div className="agreement-selector">
                                            <button type="button" className="selector-trigger" onClick={() => { setAgreementModalOpen(true); setAgreementSearch(''); }}>
                                                {selectedAgreement ? `#${selectedAgreement.id.substring(0, 6)} · ${formatCurrency(selectedAgreement.netInvestmentAmount ?? selectedAgreement.amount ?? 0)}` : 'Buscar acuerdo aprobado'}
                                            </button>
                                            {selectedAgreement && (
                                                <button type="button" className="selector-reset" onClick={() => { setSelectedAgreementId(''); setAgreementAdjustmentAmount(''); setAgreementAdjustmentReason(''); }}>
                                                    Quitar
                                                </button>
                                            )}
                                        </div>
                                        {selectedAgreement && (
                                            <small className="selector-caption">
                                                Capital {formatCurrency(selectedAgreement.netInvestmentAmount ?? selectedAgreement.amount ?? 0)} · Valor actual {formatCurrency(selectedAgreement.currentValue ?? selectedAgreement.netInvestmentAmount ?? selectedAgreement.amount ?? 0)}
                                            </small>
                                        )}
                                    </div>
                                    <div className="preset-chip-group">
                                        {AGREEMENT_PRESETS.map((preset) => (
                                            <button
                                                key={preset.id}
                                                type="button"
                                                className="preset-chip"
                                                onClick={() => applyAgreementPreset(preset)}
                                            >
                                                {preset.label}
                                            </button>
                                        ))}
                                    </div>
                                    <div className="form-grid">
                                        <div className="input-group">
                                            <label>Monto a Ajustar (+/- $)</label>
                                            <input type="number" placeholder="Ej: 50 o -25" value={agreementAdjustmentAmount} onChange={(e) => setAgreementAdjustmentAmount(e.target.value)} />
                                        </div>
                                        <div className="input-group full-width">
                                            <label>Motivo del Ajuste (mín. 5 caracteres)</label>
                                            <input type="text" placeholder="Ej: Corrección manual" value={agreementAdjustmentReason} onChange={(e) => setAgreementAdjustmentReason(e.target.value)} />
                                        </div>
                                    </div>
                                    {agreementPreview && (
                                        <div className="balance-preview balance-preview--agreement">
                                            <div>
                                                <span>Valor actual</span>
                                                <strong>{formatCurrency(agreementPreview.currentValue)}</strong>
                                            </div>
                                            <div>
                                                <span>Cambio</span>
                                                <strong className={agreementPreview.delta >= 0 ? 'positive' : 'negative'}>{formatSignedCurrency(agreementPreview.delta)}</strong>
                                            </div>
                                            <div>
                                                <span>Después del ajuste</span>
                                                <strong>{formatCurrency(agreementPreview.projectedValue)}</strong>
                                            </div>
                                            <div>
                                                <span>Tope 200%</span>
                                                <strong>{formatCurrency(agreementPreview.capAmount)}</strong>
                                            </div>
                                        </div>
                                    )}
                                    <button
                                        onClick={handleBalanceAdjustment}
                                        className="login-button"
                                        style={{ width: '100%', marginTop: '1rem', backgroundColor: '#5a67d8' }}
                                        disabled={processingId === selectedUser.id || !isAgreementReady}
                                    >
                                        {processingId === selectedUser.id ? 'Procesando...' : 'Realizar Ajuste'}
                                    </button>
                                    {agreementFeedback && (
                                        <div className={`feedback-inline feedback-inline--${agreementFeedback.type}`}>
                                            {agreementFeedback.message}
                                        </div>
                                    )}
                                </div>
                            )}

                            {showSection('security') && (
                                <div className="admin-edit-card security-actions">
                                    <h3 className="section-subtitle">Acciones de Seguridad</h3>
                                    <p>Establece una contraseña temporal para el usuario si ha perdido el acceso.</p>
                                    <button 
                                        className="login-button danger" 
                                        onClick={handleChangePassword} 
                                        disabled={processingId === selectedUser.id}
                                        style={{width: '100%', marginTop: '1rem'}}
                                    >
                                        {processingId === selectedUser.id ? 'Cambiando...' : 'Establecer Contraseña Temporal'}
                                    </button>
                                </div>
                            )}

                            {isAgreementModalOpen && (
                                <div
                                    className="admin-modal-backdrop"
                                    role="dialog"
                                    aria-modal="true"
                                    onClick={(event) => {
                                        if (event.target === event.currentTarget) {
                                            setAgreementModalOpen(false);
                                        }
                                    }}
                                >
                                    <div className="admin-modal">
                                        <div className="modal-header">
                                            <h3>Acuerdos del usuario</h3>
                                            <button type="button" className="modal-close" onClick={() => setAgreementModalOpen(false)} aria-label="Cerrar selector de acuerdos">×</button>
                                        </div>
                                        <div className="modal-body">
                                            <input
                                                type="text"
                                                className="modal-search"
                                                placeholder="Buscar por ID, monto o progreso..."
                                                value={agreementSearch}
                                                onChange={(e) => setAgreementSearch(e.target.value)}
                                            />
                                            <div className="modal-table-wrapper">
                                                {filteredAgreements.length > 0 ? (
                                                    <table className="modal-table">
                                                        <thead>
                                                            <tr>
                                                                <th>ID</th>
                                                                <th>Capital</th>
                                                                <th>Valor actual</th>
                                                                <th>Progreso</th>
                                                                <th></th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {filteredAgreements.map((agreement) => {
                                                                const baseAmount = Number(agreement.netInvestmentAmount ?? agreement.amount ?? 0);
                                                                const currentValue = Number(agreement.currentValue ?? baseAmount);
                                                                const progress = baseAmount > 0 ? Math.min(((currentValue - baseAmount) / baseAmount) * 100, 200) : 0;

                                                                return (
                                                                    <tr key={agreement.id} className={selectedAgreementId === agreement.id ? 'selected' : ''}>
                                                                        <td>#{agreement.id.substring(0, 6)}</td>
                                                                        <td>{formatCurrency(baseAmount)}</td>
                                                                        <td>{formatCurrency(currentValue)}</td>
                                                                        <td>{progress.toFixed(1)}%</td>
                                                                        <td>
                                                                            <button
                                                                                type="button"
                                                                                className="table-select-button"
                                                                                onClick={() => {
                                                                                    setSelectedAgreementId(agreement.id);
                                                                                    setAgreementSearch('');
                                                                                    setAgreementModalOpen(false);
                                                                                }}
                                                                            >
                                                                                Seleccionar
                                                                            </button>
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                ) : (
                                                    <p className="movement-empty">No se encontraron acuerdos con ese criterio.</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="empty-state-text"><h4>Selecciona un usuario de la lista para ver y editar sus detalles.</h4></div>
                    )
                }
            />
        </div>
    );
};

export default EditarPerfilAdmin;

// --- COMPONENTE: Tabla moderna de historial de saldos ---
function ModernBalanceHistoryTable({ history }) {
    if (!history || history.length === 0) {
        return <p className="movement-empty">No hay historial de saldo para este usuario.</p>;
    }
    return (
        <div className="balance-history-table-wrapper">
            <table className="balance-history-table">
                <thead>
                    <tr>
                        <th>Fecha</th>
                        <th>Tipo</th>
                        <th className="align-right">Monto</th>
                        <th className="align-right">Saldo Antes</th>
                        <th className="align-right">Saldo Después</th>
                        <th>Descripción</th>
                    </tr>
                </thead>
                <tbody>
                    {history.map((row) => (
                        <tr key={row.id}>
                            <td>{row.fecha ? row.fecha.toLocaleString('es-ES') : 'Sin fecha'}</td>
                            <td><span className="type-badge">{row.type}</span></td>
                            <td className={"align-right " + (row.amount >= 0 ? 'positive' : 'negative')}>{row.amount > 0 ? '+' : ''}{row.amount.toFixed(2)}</td>
                            <td className="align-right">{row.saldoAntes.toFixed(2)}</td>
                            <td className="align-right">{row.saldoDespues.toFixed(2)}</td>
                            <td>{row.description}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}